import useSWR from 'swr';
import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import { IEvaluation } from '@/lib/evaluationsApi';
import { IRepository } from '@/types';
import { TViewMode } from '@/components/DataFrame/types';

import { fetchRemoteDataframe } from '@/lib/workspaces/tabularApi';
import { IDataFrameResponse } from '@/lib/dataFrameTypes';

const SHORT_POLLING_INTERVAL = 1 * 1000; // 1 second
const LONG_POLLING_INTERVAL = 10 * 1000; // 10 seconds

interface useRmodDataframeParams {
  repository: IRepository;
  evaluation: IEvaluation;
  initialData?: IDataFrameResponse | undefined;
}

const useRmodDataframe = ({ repository, evaluation, initialData }: useRmodDataframeParams) => {
  const { status, resource, workspace: workspace } = evaluation;
  const { path, version: branch } = resource;

  // TODO: Make the bailout status more universal
  const shouldBailout = ['completed', 'error', 'cancelled'].includes(evaluation.status);

  if (initialData && shouldBailout) {
    console.log('useRmodDataframe > bailout');
    return { isLoading: false, isValidating: false, error: undefined, data: initialData, revalidate: () => {} };
  }
  const workspaceId = workspace?.id;

  const { namespace, name: repositoryName } = repository;
  const fetchKey = ['rmod_data_frame', namespace, repositoryName, branch, workspaceId, evaluation.id];

  const fetcher = async () => {
    const result = await fetchRemoteDataframe({ repository, path, query: {}, workspaceId: workspaceId! });
    if (result.status !== 'success') {
      const error = new Error('An error occurred while fetching the data.');
      throw error;
    }
    return result;
  };

  // console.log('useRmodDataframe > fetchKey', fetchKey);

  const {
    data,
    mutate,
    error: requestError,
    isLoading,
    isValidating,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useSWR(fetchKey, () => fetcher(), {
    fallbackData: initialData,
    keepPreviousData: true,
    focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    shouldRetryOnError: true,
    errorRetryCount: 3,
    refreshInterval: shouldBailout ? undefined : LONG_POLLING_INTERVAL,
  });

  const revalidate = () => {
    mutate();
  };

  return {
    isLoading,
    isValidating,
    error: requestError,
    data,
    revalidate,
  };
};

export default useRmodDataframe;
